import React, { useMemo, useState } from 'react';
// @ts-ignore
import styles from './MedicalRequestCreate.module.scss';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import MedicalRequestCreateForm from './MedicalRequestCreateForm';
import Loading from '../../../../Components/Loading';
import { ItemScheme } from '../../../../Components/Form/MagicFormItem';
import {
	useMedicalRequestFormsQuery,
	useSubmitWizardMutation,
} from '../../../../graphql/graphql';
import { get } from '../../../../Services/ApiClient';
import usePatientConsent from '../../../../Components/PatientProfile/Hooks/usePatientConsent';
import { MagicField } from '../../../../Enums/MagicField.enum';
import { useEffectAsync } from '../../../../helpers';

interface Props {
	patientId: string;
	workplaceId: string;
	onSuccess: () => void;
	autoOpenByKey?: string;
	caseId?: string;
}

interface medicalRequestForm {
	id: string;
	name?: string;
	type?: string;
	wizardKey?: string;
}

const MedicalRequestCreate: React.FC<Props> = ({
	patientId,
	workplaceId,
	onSuccess,
	autoOpenByKey,
	caseId,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [formScheme, setFormScheme] = useState<ItemScheme[]>();
	const [defaultValues, setDefaultValues] = useState({});
	const medicalRequestForms = useMedicalRequestFormsQuery();
	const [submitWizard] = useSubmitWizardMutation();
	const [selectedWizardKey, setSelectedWizardKey] = useState<
		string | undefined
	>(undefined);
	const { isConsentProvided } = usePatientConsent(patientId);

	useEffectAsync(async () => {
		if (autoOpenByKey) {
			await pickMedicalRequest(autoOpenByKey);
		}
	}, [autoOpenByKey]);

	const availableForms = useMemo(() => {
		const tempForms: medicalRequestForm[] = [];
		medicalRequestForms.data?.medicalRequestForms?.data?.forEach(item => {
			tempForms.push({
				id: item?.id,
				name: item?.name ?? undefined,
				type: item?.type ?? undefined,
				wizardKey: item?.wizardKey ?? undefined,
			});
		});
		return tempForms;
	}, [medicalRequestForms]);

	const pickMedicalRequest = async (wizardKey: string) => {
		setIsLoading(true);
		const wizard: any = await get(
			`/wizard/${wizardKey}/tree?userId=${patientId}`,
		);
		if (wizard) {
			let tempScheme: ItemScheme[] = [];
			if (!isConsentProvided) {
				tempScheme.push(
					{
						type: MagicField.PATIENT_CONSENT_REQUEST,
						settings: {
							patientId,
						},
					},
					{
						type: MagicField.WHITESPACE,
					},
				);
			}
			tempScheme = [...tempScheme, ...wizard?.data?.children];
			if (isConsentProvided) {
				tempScheme.push(
					{
						type: MagicField.WHITESPACE,
					},
					{
						type: MagicField.CHECKBOX,
						key: 'addToPatientECard',
						title: 'component.medicalRequestCreate.addToPatientsECard',
					},
				);
			}
			setFormScheme(tempScheme);
			const tempDefaultValues = wizard?.data?.settings?.defaultValues ?? {};
			if (isConsentProvided) {
				tempDefaultValues.addToPatientECard = true;
			}
			setDefaultValues(tempDefaultValues);
			setSelectedWizardKey(wizardKey);
		}
		setIsLoading(false);
	};

	const prepareValues = (values: any, scheme: ItemScheme[]) => {
		let data: { wizardItemId: string; data: object }[] = [];
		scheme?.forEach(item => {
			if (item?.id && item?.key) {
				data.push({
					wizardItemId: item.id,
					data: { rawValue: values[item?.key] },
				});
			}
			if (item.children?.length) {
				data = [
					...data,
					...prepareValues(values, item.children as ItemScheme[]),
				];
			}
		});
		return data;
	};

	const handleOnSubmit = async (values: any) => {
		if (formScheme) {
			const data = prepareValues(values, formScheme);
			if (selectedWizardKey) {
				setIsLoading(true);
				const result = await submitWizard({
					input: {
						wizardKey: selectedWizardKey,
						userId: patientId,
						items: data,
						addToPatientECard: values?.addToPatientECard,
						caseId,
					},
				});
				if (result) {
					setTimeout(() => {
						setIsLoading(false);
					}, 2000);
					setSelectedWizardKey(undefined);
					setDefaultValues({});
					onSuccess();
				}
			}
		}
	};

	if (isLoading || medicalRequestForms.isLoading) {
		return <Loading />;
	}

	if (!formScheme) {
		return (
			<div className={styles.wrapper}>
				{availableForms.map(({ name, wizardKey }, key) => {
					if (name && wizardKey) {
						return (
							<div className={styles.item} key={key}>
								<div className={styles.title}>{name}</div>
								<div>
									<Button
										theme={Theme.GREEN}
										title='component.medicalRequestForms.exhibit'
										onClick={() => pickMedicalRequest(wizardKey)}
									/>
								</div>
							</div>
						);
					}
				})}
			</div>
		);
	}

	return (
		<MedicalRequestCreateForm
			formScheme={formScheme}
			defaultValues={defaultValues}
			onSubmit={handleOnSubmit}
		/>
	);
};

export default MedicalRequestCreate;
